import apiClient from './apiClient';

// Owner interfaces
export interface Owner {
  id: string;
  username: string;
  isAdmin: boolean;
  roles: string[];
  createdAt: string;
  updatedAt: string;
}

export interface Feature {
  id: string;
  name: string;
  description: string;
}

export interface CreateOwnerRequest {
  username: string;
  password?: string;
  isAdmin?: boolean;
}

export interface UpdateOwnerRequest {
  username?: string;
  password?: string;
  isAdmin?: boolean;
}

export interface OwnersFilterParams {
  username?: string;
  isAdmin?: boolean;
  page?: number;
  limit?: number;
}

interface OwnersPaginatedResponse {
  items: Owner[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// CRUD operations
export const fetchOwners = async (filters: OwnersFilterParams = {}): Promise<OwnersPaginatedResponse> => {
  try {
    const response = await apiClient.get<OwnersPaginatedResponse>(`/owners`, { params: filters });
    return response.data;
  } catch (error) {
    console.error('Error fetching owners:', error);
    throw error;
  }
};

export const getOwnerById = async (id: string): Promise<Owner> => {
  try {
    const response = await apiClient.get<Owner>(`/owners/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching owner with ID ${id}:`, error);
    throw error;
  }
};

export const createOwner = async (ownerData: CreateOwnerRequest): Promise<Owner> => {
  try {
    const response = await apiClient.post<Owner>('/owners', ownerData);
    return response.data;
  } catch (error) {
    console.error('Error creating owner:', error);
    throw error;
  }
};

export const updateOwner = async (id: string, ownerData: UpdateOwnerRequest): Promise<Owner> => {
  try {
    const response = await apiClient.patch<Owner>(`/owners/${id}`, ownerData);
    return response.data;
  } catch (error) {
    console.error(`Error updating owner with ID ${id}:`, error);
    throw error;
  }
};

export const deleteOwner = async (id: string): Promise<void> => {
  try {
    await apiClient.delete(`/owners/${id}`);
  } catch (error) {
    console.error(`Error deleting owner with ID ${id}:`, error);
    throw error;
  }
};

// Feature flag management
export const getAllFeatures = async (): Promise<Feature[]> => {
  try {
    const response = await apiClient.get<Feature[]>('/admin/features');
    return response.data;
  } catch (error) {
    console.error('Error fetching all features:', error);
    throw error;
  }
};

export const getOwnerFeatures = async (ownerId: string): Promise<Feature[]> => {
  try {
    const response = await apiClient.get<Feature[]>(`/admin/owners/${ownerId}/features`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching features for owner ${ownerId}:`, error);
    throw error;
  }
};

export const assignFeatureToOwner = async (ownerId: string, featureId: string): Promise<void> => {
  try {
    await apiClient.post(`/admin/owners/${ownerId}/features`, { featureId });
  } catch (error) {
    console.error(`Error assigning feature ${featureId} to owner ${ownerId}:`, error);
    throw error;
  }
};

export const removeFeatureFromOwner = async (ownerId: string, featureId: string): Promise<void> => {
  try {
    await apiClient.delete(`/admin/owners/${ownerId}/features/${featureId}`);
  } catch (error) {
    console.error(`Error removing feature ${featureId} from owner ${ownerId}:`, error);
    throw error;
  }
};
